import { Component, OnInit, OnDestroy, Input, inject, signal } from '@angular/core';
import { ProjectV2ServiceService } from '../../../../shared/services/project-v2-service.service';
import { Subject, Subscription, throwError, take } from 'rxjs';
import * as htmlToImage from 'html-to-image';
import { OverlayService } from '../../../../shared/services/overlayService';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../shared/services/auth.service';
import { TimelineService } from '../../../../shared/services/timeline.service';
import { FeatureCriteriaForm, FeatureTypes, TimelineDisplayMode, Html2PdfOptions } from '../../../../shared/interfaces';
import html2pdf from 'html2pdf.js';
import { FeatureConfigService } from '../../../../shared/services/feature-config.service';

import {
  TIMELINE_COLORS,
  TIMELINE_PAGE_IDS,
  TIMELINE_HEADERS,
  TIMELINE_FILES,
  PDF_CONFIG,
  PDF_CONSTANTS,
  FILE_CONSTANTS,
  CRITERIA_CONSTANTS,
  IMAGE_CONSTANTS,
  NOTIFY,
} from '../models/recap-timeline-constant';

@Component({
  selector: 'mtm-download-timeline',
  templateUrl: './download-timeline.component.html',
  styleUrls: ['./download-timeline.component.scss', '../recap.component.scss', '../../timeline-style.scss'],
  standalone: false,
})
export class DownloadTimelineComponent implements OnInit, OnDestroy {
  private readonly featureConfigService = inject(FeatureConfigService);

  private readonly visualizationsSignal = signal<any[]>([]);
  private readonly timelineDataSignal = signal<any>(null);
  private readonly isFullTimelineSignal = signal<boolean>(false);
  private readonly isBothSelectedSignal = signal<boolean>(false);
  private readonly timelineDisplayModeSignal = signal<TimelineDisplayMode>(TimelineDisplayMode.Horizontal);
  private readonly showLoaderSignal = signal<boolean>(false);
  private readonly generatedImagesSignal = signal<{ title: string; base64Image: string }[]>([]);
  private readonly extensionSignal = signal<string>(PDF_CONSTANTS.DEFAULT_EXTENSION);
  private readonly isCriteriaForm1EnabledSignal = signal<boolean>(false);
  private readonly isCriteriaForm2EnabledSignal = signal<boolean>(false);
  private readonly isSimplifiedCriteriaEnabledSignal = signal<boolean>(false);

  get visualizations(): any[] {
    return this.visualizationsSignal();
  }
  @Input()
  set visualizations(value: any[]) {
    this.visualizationsSignal.set(value ?? []);
  }

  get timelineData(): any {
    return this.timelineDataSignal();
  }
  @Input()
  set timelineData(value: any) {
    this.timelineDataSignal.set(value ?? null);
  }

  get isFullTimeline(): boolean {
    return this.isFullTimelineSignal();
  }
  @Input()
  set isFullTimeline(value: boolean) {
    this.isFullTimelineSignal.set(!!value);
  }

  get isBothSelected(): boolean {
    return this.isBothSelectedSignal();
  }
  @Input()
  set isBothSelected(value: boolean) {
    this.isBothSelectedSignal.set(!!value);
  }

  get timelineDisplayMode(): TimelineDisplayMode {
    return this.timelineDisplayModeSignal();
  }
  set timelineDisplayMode(value: TimelineDisplayMode) {
    this.timelineDisplayModeSignal.set(value ?? TimelineDisplayMode.Horizontal);
  }

  get showLoader(): boolean {
    return this.showLoaderSignal();
  }
  set showLoader(value: boolean) {
    this.showLoaderSignal.set(!!value);
  }

  get generatedImages(): { title: string; base64Image: string }[] {
    return this.generatedImagesSignal();
  }
  set generatedImages(value: { title: string; base64Image: string }[]) {
    this.generatedImagesSignal.set(value ?? []);
  }

  get extension(): string {
    return this.extensionSignal();
  }
  set extension(value: string) {
    this.extensionSignal.set(value || PDF_CONSTANTS.DEFAULT_EXTENSION);
  }

  get isCriteriaForm1Enabled(): boolean {
    return this.isCriteriaForm1EnabledSignal();
  }
  set isCriteriaForm1Enabled(value: boolean) {
    this.isCriteriaForm1EnabledSignal.set(!!value);
  }

  get isCriteriaForm2Enabled(): boolean {
    return this.isCriteriaForm2EnabledSignal();
  }
  set isCriteriaForm2Enabled(value: boolean) {
    this.isCriteriaForm2EnabledSignal.set(!!value);
  }

  get isSimplifiedCriteriaEnabled(): boolean {
    return this.isSimplifiedCriteriaEnabledSignal();
  }
  set isSimplifiedCriteriaEnabled(value: boolean) {
    this.isSimplifiedCriteriaEnabledSignal.set(!!value);
  }

  @Input() productTimeline: any = [];
  @Input() campaignTimeline: any = [];

  subscription: Subscription;
  summaryDetail: any;

  colors = TIMELINE_COLORS;
  pageIds = TIMELINE_PAGE_IDS;
  ngUnsubscribe = new Subject();

  headerImage = TIMELINE_HEADERS.defaultImage;
  headerLogo = TIMELINE_HEADERS.defaultLogo;
  headerTitle = TIMELINE_HEADERS.defaultTitle;
  headerSubtitle = TIMELINE_HEADERS.defaultSubtitle;

  scenario3YslHeader = TIMELINE_HEADERS.yslHeader;
  scenario3YslHeaderLogo = TIMELINE_HEADERS.yslLogo;

  authUser: any;
  format: string = PDF_CONSTANTS.DEFAULT_FORMAT;

  constructor(
    private readonly projectV2Service: ProjectV2ServiceService,
    private readonly overlayService: OverlayService,
    private readonly authService: AuthService,
    public timelineService: TimelineService
  ) {}

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.subscription = this.projectV2Service.currentVisibility.subscribe((result) => {
      this.isFullTimeline = result;
    });

    this.summaryDetail = JSON.parse(this.timelineData.timelineCriteria);

    this.headerImage = this.scenario3YslHeader;
    this.headerLogo = this.scenario3YslHeaderLogo;
    this.headerTitle = this.timelineService.workspaceName;
    this.headerSubtitle = this.timelineData.name;

    this.timelineService.timelineDisplay$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((mode) => {
      this.timelineDisplayMode = mode;
    });

    this.changeTimelineDisplay(TimelineDisplayMode.VerticalList);

    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_TIMELINE])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        const campaignTimelineConfig = response[FeatureTypes.FEA_CAMPAIGN_TIMELINE];
        this.isCriteriaForm1Enabled =
          campaignTimelineConfig?.criteriaForm === FeatureCriteriaForm.CRITERIA_FORM_TIMELINE_1;
        this.isCriteriaForm2Enabled =
          campaignTimelineConfig?.criteriaForm === FeatureCriteriaForm.CRITERIA_FORM_TIMELINE_2;
        this.isSimplifiedCriteriaEnabled =
          campaignTimelineConfig?.criteriaForm === CRITERIA_CONSTANTS.SIMPLIFIED_1 ||
          campaignTimelineConfig?.criteriaForm === CRITERIA_CONSTANTS.SIMPLIFIED_2;
      });
  }

  ngOnDestroy(): void {
    this.timelineService.timelineDisplay$.next(TimelineDisplayMode.Horizontal);
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  async generateImage(id: string) {
    const element = document.getElementById(id);
    if (!element) return;

    this.showLoader = true;
    try {
      const blob = await htmlToImage.toBlob(element);
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        this.overlayService.showSuccess(NOTIFY.COPY_IMAGE_SUCCESS, NOTIFY.COPY_IMAGE_TITLE);
      } else {
        this.overlayService.showError(NOTIFY.COPY_IMAGE_ERROR, NOTIFY.COPY_IMAGE_ERROR_TITLE);
      }
    } catch {
      this.overlayService.showError(NOTIFY.COPY_IMAGE_ERROR, NOTIFY.COPY_IMAGE_ERROR_TITLE);
    } finally {
      this.showLoader = false;
    }
  }

  async downloadImages() {
    this.showLoader = true;

    if (this.extension === PDF_CONSTANTS.DEFAULT_EXTENSION) {
      this.generatePDF();
      return;
    }

    const excludeSection = document.getElementById(PDF_CONSTANTS.PDF_EXCLUDE_SECTION_ID);
    if (excludeSection) excludeSection.style.display = 'none';

    this.generatedImages = [] as { title: string; base64Image: string }[];
    await Promise.all(
      this.pageIds.map(async (elementId) => {
        const element = document.getElementById(elementId);
        if (!element) return;

        try {
          await document.fonts.ready;
          const dataUrl = await htmlToImage.toPng(element, {
            skipFonts: true,
            backgroundColor: '#ffffff',
          });
          this.generatedImages.push({
            title: `${IMAGE_CONSTANTS.TITLE_PREFIX} ${elementId}`,
            base64Image: dataUrl.split(IMAGE_CONSTANTS.BASE64_PREFIX)[1],
          });
        } catch (error) {
          console.error('Error generating image for', elementId, error);
        }
      })
    );

    if (excludeSection) excludeSection.style.display = '';

    this.generatedImages.sort((a, b) => a.title.localeCompare(b.title));

    if (this.generatedImages.length > 0) {
      this.downloadPpt();
    } else {
      this.showLoader = false;
    }
  }

  handleFileExtension(event: Event) {
    this.extension = (event.target as HTMLSelectElement).value;
  }

  changeTimelineDisplay(mode: TimelineDisplayMode) {
    if (this.timelineDisplayMode !== mode) {
      this.timelineService.timelineDisplay$.next(mode);
    }
  }

  downloadPpt(): void {
    if (!this.generatedImages.length) {
      console.warn('No images generated');
      return;
    }
    this.showLoader = true;

    this.projectV2Service
      .downloadPPT({ items: this.generatedImages })
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Error while generating PPT:', err);
          return throwError(() => err);
        }),
        finalize(() => {
          this.showLoader = false;
        })
      )
      .subscribe((response: Blob) => {
        const blob = new Blob([response], {
          type: FILE_CONSTANTS.MIME_PPTX,
        });

        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = URL.createObjectURL(blob);
        a.download = TIMELINE_FILES.pptFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(a.href);
      });
  }

  generatePDF(): void {
    const data = document.getElementById(PDF_CONSTANTS.PDF_CONTENT_ID);
    const excludeSection = document.getElementById(PDF_CONSTANTS.PDF_EXCLUDE_SECTION_ID);

    if (!data) return;

    this.showLoader = true;
    if (excludeSection) excludeSection.style.display = 'none';

    const { pageWidthMm, pageHeightMm, topMarginMm, bottomMarginMm } = PDF_CONFIG;

    const options: Html2PdfOptions = {
      margin: [topMarginMm, 15, bottomMarginMm, 15],
      filename: TIMELINE_FILES.pdfFileName,
      image: { type: IMAGE_CONSTANTS.TYPE, quality: 2 },
      jsPDF: {
        unit: 'mm',
        format: [pageWidthMm, pageHeightMm],
        orientation: PDF_CONSTANTS.ORIENTATION,
      },
    };

    html2pdf()
      .set(options)
      .from(data)
      .toPdf()
      .get(FILE_CONSTANTS.PDF_TYPE)
      .then((pdf: any) => {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.text(`${i}/${totalPages}`, pageWidthMm / 2, pageHeightMm - 10, { align: 'center' });
        }

        const pdfBlob = pdf.output('blob');
        const pdfURL = URL.createObjectURL(pdfBlob);

        const a = document.createElement('a');
        a.href = pdfURL;
        a.download = TIMELINE_FILES.pdfFileName;
        a.click();

        window.open(pdfURL, '_blank');
      })
      .finally(() => {
        this.showLoader = false;
        if (excludeSection) excludeSection.style.display = '';
      })
      .catch((error: any) => {
        console.error('Error generating PDF:', error);
      });
  }
}
